// Microsoft auth helpers — wraps MSAL.js. Single source of truth for
// sign in, sign out, getting access tokens, and active-account state.

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { loginRequest, msalConfig, msalConfigured } from './msalConfig';

export const msalInstance = msalConfigured ? new PublicClientApplication(msalConfig) : null;

let initialized = false;
let initPromise: Promise<boolean> | null = null;

/**
 * Initialize MSAL exactly once. Idempotent — subsequent callers get the same promise.
 * Returns false if MSAL isn't configured (no client ID env var); callers should branch.
 */
export async function ensureMsalInitialized(): Promise<boolean> {
  if (!msalInstance) return false;
  if (initialized) return true;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    await msalInstance!.initialize();
    await msalInstance!.handleRedirectPromise();
    initialized = true;
    return true;
  })();
  return initPromise;
}

/**
 * Sign in with Microsoft via REDIRECT flow.
 *
 * Why not popup? Popup flow was loading the full app in the popup window,
 * which caused racing handleRedirectPromise() calls between opener + popup
 * and threw `no_token_request_cache_error`. Redirect flow uses a single
 * window — auth code returns to the same page that issued it. Bulletproof.
 *
 * Downside: the user is briefly navigated away from the app to login.microsoftonline.com
 * and back. For a daily-use shop tool that's fine; they sign in once a session.
 *
 * Returns a never-resolving Promise — the browser navigates away before the call completes.
 */
export async function signIn(): Promise<void> {
  if (!(await ensureMsalInitialized()) || !msalInstance) {
    throw new Error(
      'MSAL not configured. Set VITE_MSAL_CLIENT_ID + VITE_MSAL_TENANT_ID in .env.local — see src/shared/msalConfig.ts for setup steps.',
    );
  }
  await msalInstance.loginRedirect(loginRequest);
  // Code after this won't execute — the browser is navigating away to Microsoft.
}

export async function signOut(): Promise<void> {
  if (!msalInstance) return;
  await ensureMsalInitialized();
  await msalInstance.logoutRedirect();
}

/**
 * Current signed-in account, or null. Reads from MSAL's cache, so it's
 * available even on first paint (no async needed).
 */
export function getActiveAccount(): AccountInfo | null {
  if (!msalInstance || !initialized) return null;
  const active = msalInstance.getActiveAccount();
  if (active) return active;
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  msalInstance.setActiveAccount(accounts[0]);
  return accounts[0];
}

/**
 * Get an access token for the configured scopes (User.Read, Mail.ReadWrite, offline_access).
 * Tries silent acquisition first; falls back to interactive popup if needed.
 */
export async function getAccessToken(): Promise<string> {
  if (!(await ensureMsalInitialized()) || !msalInstance) {
    throw new Error('MSAL not configured.');
  }
  const account = getActiveAccount();
  if (!account) {
    throw new Error('Not signed in. Sign in with Microsoft first.');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Silent refresh failed — must re-auth interactively. Use redirect (matches signIn flow).
      await msalInstance.acquireTokenRedirect(loginRequest);
      // Browser navigates away; this throw is just to satisfy the type
      throw new Error('Token refresh required — redirecting to Microsoft sign-in…');
    }
    throw err;
  }
}
